import { Component, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { map, take, combineLatestWith } from 'rxjs/operators';

import { StepperService } from '../../services/stepper.service';
import { TgaState } from '../../shared/models/models';
import { selectActiveStepName } from '../../store/tga.selectors';
import { PhaseContainerComponent } from '../phase-container/phase-container.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-stepper',
    templateUrl: './stepper.component.html',
    styleUrls: ['./stepper.component.scss'],
    imports: [PhaseContainerComponent, AsyncPipe],
})
export class StepperComponent implements OnDestroy {
  private readonly stepperService = inject(StepperService);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<TgaState>>(Store);

  private readonly destroy$ = new Subject<void>();

  readonly questionnaireType$ = this.route.url.pipe(
    map((url) => {
      if (url[3]?.path !== 'questionnaire') return undefined;
      const pathValue = url[4]?.path;
      if (pathValue === 'generic') return 'generic';
      if (pathValue === 'specific') return 'specific';
      return undefined;
    }),
  );

  readonly phases$ = this.stepperService.getPhasesFromRoute(this.questionnaireType$).pipe(
    combineLatestWith(this.store.select(selectActiveStepName)),
    map(([phases, activeStepName]) => {
      const absNumber = this.stepperService.getAbsoluteStepNumberByName(activeStepName);
      return this.stepperService.getUpdatedPhasesForStepClick(phases, absNumber);
    }),
  );

  onStepClick = (clickedStepNumber: number): void => {
    this.phases$.pipe(take(1)).subscribe((phases) => {
      const allSteps = phases.flatMap((phase) => phase.steps);
      const targetStep = allSteps[clickedStepNumber - 1];

      if (targetStep.enabled ?? true) {
        this.stepperService.moveToStep(targetStep.translationKey);
      }
    });
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
